import { useEffect, useState } from 'react';
import { FormInstance, message } from 'antd';
import { get, post } from '../../utils/fetch';

type Integration =
  | {
      id?: string;
      enabled: boolean;
    }
  | undefined;

type OnConnectedStateLoaded = (results: Record<string, any>) => void;

type GetConnectedStateOptions = {
  errorMessage?: string | false;
};

type SaveSettingsOptions = {
  form?: FormInstance;
  beforeSave?: () => Promise<void>;
  afterSave?: () => void;
  reloadAfter?: boolean;
  successMessage?: string;
  errorMessage?: string;
};

type DisconnectOptions = {
  reloadAfter?: boolean;
  deleteFallback?: boolean;
  successMessage?: string;
  errorMessage?: string;
};

type Options = {
  organizationId: string;
  projectId: string;
  integration: Integration;
  // e.g. 'Github', 'Asana', 'Slack', 'Jira', 'Clickup', 'Team', 'Teamwork', 'Trello'
  integrationCode: string;
  onDisconnect?: () => void;
  onConnectedStateLoaded?: OnConnectedStateLoaded;
};

type HookReturn = {
  integrationId: string;
  projectIntegrationId: string;
  isEnabled: boolean;
  setIsEnabled: (v: boolean) => void;
  getConnectedState: (options?: GetConnectedStateOptions) => Promise<void>;
  saveSettings: (
    settings: Record<string, any>,
    options?: SaveSettingsOptions
  ) => Promise<void>;
  disconnect: (options?: DisconnectOptions) => Promise<void>;
};

export const useIntegrationConnection = ({
  organizationId,
  projectId,
  integration,
  integrationCode,
  onDisconnect,
  onConnectedStateLoaded
}: Options): HookReturn => {
  const [integrationId, setIntegrationId] = useState(integration?.id || '');
  const [projectIntegrationId, setProjectIntegrationId] = useState('');
  const [isEnabled, setIsEnabled] = useState(integration?.enabled ?? false);
  const baseUrl = `/organizations/${organizationId}/integrations`;

  useEffect(() => {
    if (integration?.id) setIntegrationId(integration.id);
  }, [integration?.id]);

  const getConnectedState = async (options: GetConnectedStateOptions = {}) => {
    if (!integration?.enabled) return;
    const { errorMessage = 'Failed to get integration connection state.' } = options;
    try {
      const results = await get(
        `${baseUrl}/integration_connection?project_id=${projectId}&integration_code=${integrationCode}`
      );
      setIntegrationId(results.integration_id || integration?.id || '');
      setProjectIntegrationId(results.project_integration_id);
      onConnectedStateLoaded?.(results);
    } catch (error) {
      if (errorMessage !== false) message.error(errorMessage);
    }
  };

  const saveSettings = async (
    settings: Record<string, any>,
    options: SaveSettingsOptions = {}
  ) => {
    const {
      form,
      beforeSave,
      afterSave,
      reloadAfter = false,
      successMessage = 'Settings saved successfully.',
      errorMessage = 'Something went wrong while saving. Please try again.'
    } = options;
    try {
      if (form) await form.validateFields();
      if (beforeSave) await beforeSave();

      const activeIntegrationId = integrationId || integration?.id;
      if (!activeIntegrationId) throw new Error('Integration is not connected.');

      await post(
        `${baseUrl}/${activeIntegrationId}/project_integrations/save_settings?integration_code=${integrationCode}`,
        {
          project_id: projectId,
          project_integration_id: projectIntegrationId,
          settings
        }
      );

      afterSave?.();
      message.success(successMessage, 5);
      if (reloadAfter) window.location.reload();
    } catch (error) {
      console.error('Error saving settings', error);
      if ((error as { errorFields?: unknown })?.errorFields) {
        message.error('Please fill in all required fields.');
      } else {
        message.error(errorMessage);
      }
    }
  };

  const disconnect = async (options: DisconnectOptions = {}) => {
    const {
      reloadAfter = false,
      deleteFallback = false,
      successMessage = 'Integration disconnected successfully.',
      errorMessage = 'Failed to disconnect. Please try again.'
    } = options;
    try {
      if (projectIntegrationId) {
        const activeIntegrationId = integrationId || integration?.id;
        if (!activeIntegrationId) throw new Error('Integration is not connected.');

        await post(
          `${baseUrl}/${activeIntegrationId}/project_integrations/disconnect`,
          { id: projectIntegrationId }
        );
      } else if (deleteFallback && integration?.id) {
        const csrfToken = (document.querySelector(
          'meta[name="csrf-token"]'
        ) as any)?.content;
        const response = await fetch(`${baseUrl}/${integration.id}`, {
          method: 'DELETE',
          headers: { 'X-CSRF-Token': csrfToken }
        });
        if (!response.ok) throw new Error('DELETE request failed');
      }

      message.success(successMessage, 5);
      if (!reloadAfter) {
        setIsEnabled(false);
        onDisconnect?.();
      }
    } catch (error) {
      console.error('Error disconnecting integration', error);
      message.error(errorMessage);
    } finally {
      if (reloadAfter) window.location.reload();
    }
  };

  return {
    integrationId,
    projectIntegrationId,
    isEnabled,
    setIsEnabled,
    getConnectedState,
    saveSettings,
    disconnect
  };
};
